import type { FlatRateCoverage } from '../types';
import type {
    CockpitWarning,
    ForecastInput,
    ForecastResult,
    MonthSeries,
} from './cockpitTypes';
import {
    altQuantity,
    calendarDay,
    entryCents,
    isMigrationBulkBackfill,
    isWorkEntry,
    performedDateIso,
} from './cockpitLogic';
import type { CockpitLogEntry } from './cockpitLogic';
import {
    bookedFlatMonths,
    flatMonthKey,
    isFlatRateService,
    resolveFlatCoverage,
    type FlatCoverageServiceLike,
} from './flatRateLogic';
import { roundCents } from './money';
import { contractedQuantityOfRow } from './nepPosition';
import { isBackfilledAltEntry } from './serviceLogUtils';
import { isHourlyUnit, isMonthlyUnit } from './serviceUnits';
import { istUebungsprojekt } from './uebung/uebungService';
import { buildWorkloadIst, buildWorkloadPlan, primaryInspectionRow } from './cockpitWorkload';
import { inspectionProofMatcher } from './projectLv';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FORECAST DES KPI-COCKPITS — arithmetisch statt Tagesschleife
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Plan: docs/kpi-cockpit-plan.md §2.7. Die Fachregeln kommen aus dem alten
 * Forecast in components/AdminDashboard.tsx (Zeilen 337–456); uebernommen sind
 * die RHYTHMUSREGELN (welche Felder den Turnus tragen, was als Begehung zaehlt),
 * NICHT die Simulation, die von Projektbeginn an Tag fuer Tag weiterschritt.
 *
 * WARUM KEINE TAGESSCHLEIFE MEHR: Die alte Schleife lief ab Projektbeginn und
 * brach nach `quantityContracted` Terminen ab. Fuer ein Altprojekt (Beginn
 * 2024, 30 Begehungen woechentlich) lagen diese 30 Termine allesamt in 2024 —
 * das betrachtete Jahr bekam 0 € Forecast, obwohl acht Begehungen offen waren
 * (Befund 11 des Audits). Umgekehrt lieferten beendete und abgelaufene Projekte
 * weiter, weil die Schleife den Status nie ansah. Beides macht die Kennzahl
 * „Realisierung" strukturell falsch, und zwar in beide Richtungen.
 *
 * Die neue Rechnung fragt stattdessen zwei Dinge:
 *   1. WIE VIEL ist zu Jahresbeginn ueberhaupt noch offen?
 *      Restmenge = beauftragt − tatsaechlich erbracht vor dem Jahr. Die
 *      „tatsaechlich erbrachte" Menge kommt aus dem Protokoll (isWorkEntry),
 *      nicht aus `quantity_used`: der Zaehler ist ein Lebenszeitstand ohne
 *      Datum und kann die Frage „vor diesem Jahr" nicht beantworten.
 *   2. WANN faellt das an? Der Turnus liefert die Termine je Monat im
 *      Ueberlappungsfenster; die Restmenge deckelt sie.
 *
 * Damit ist der Forecast eines Altprojekts der REST, nicht die Wiederholung
 * des ganzen Auftrags — und ein Projekt ohne Restmenge liefert 0 statt seinen
 * Vertragswert ein zweites Mal.
 *
 * REINE FUNKTION: keine React-, DOM- oder Supabase-Abhaengigkeit, `today` und
 * `year` kommen als Parameter. Aufbau wie cockpitLogic.ts.
 */

/* ═════════════════════════════════════════════════════════════════════════
 * 1. Die Form der Eingabe
 *
 * `ForecastInput.projects` ist im Vertrag `any[]` (cockpitTypes.ts, Eigentum
 * von WP1). Hier bekommt dieselbe Zeile eine ENGE lokale Form: so steht in
 * dieser Datei kein einziges `any`, und jede gelesene Spalte ist benannt.
 * Gelesen wird die rohe `project_services`-Zeile (snake_case), genau wie im
 * alten Cockpit.
 * ═══════════════════════════════════════════════════════════════════════ */

/** Eine `project_services`-Zeile, so weit der Forecast sie liest. */
export interface ForecastServiceRow {
    id?: string | null;
    title?: string | null;
    unit?: string | null;
    unit_price?: unknown;
    quantity_contracted?: unknown;
    quantity_used?: unknown;
    is_optional?: unknown;
    assignment?: string | null;
    lv_position?: string | null;
    lvPosition?: string | null;
    flat_rate?: FlatRateCoverage | null;
    flatRate?: FlatRateCoverage | null;
}

/** Ein Protokolleintrag, so weit der Forecast ihn liest. */
export interface ForecastLogEntry extends CockpitLogEntry {
    serviceId?: string | null;
    quantity?: unknown;
    /** Nur auf `ALT-`-Zeilen: die Menge, die wirklich auf den Stand gewirkt hat. */
    appliedUsed?: unknown;
    /** Berichtsbezug — macht eine Nachweiszeile auf der Pauschale zur Begehung (isInspectionProofEntry). */
    reportRef?: string | null;
}

/** Der Turnus einer Begehungsposition — Feldnamen wortgleich aus types.IntervalConfig. */
export interface ForecastIntervalConfig {
    type?: string | null;
    customDays?: number | null;
    preferredWeekdays?: number[] | null;
    /** Naechster geplanter Termin — Anker der Auslastungsplanung (cockpitWorkload). */
    nextScheduledDate?: string | null;
    /** Letzte Begehung — Ersatzanker, wenn kein naechster Termin steht. */
    lastInspectionDate?: string | null;
}

export interface ForecastGlobalData {
    status?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    serviceLog?: ForecastLogEntry[] | null;
    istUebung?: boolean | null;
}

export interface ForecastMetadata {
    coordinatorId?: string | null;
    projectType?: string | null;
    intervalConfig?: ForecastIntervalConfig | null;
    globalData?: ForecastGlobalData | null;
}

/** Eine Projektzeile, so weit der Forecast sie liest. */
export interface ForecastProject {
    id?: string | null;
    name?: string | null;
    project_number?: string | null;
    status?: string | null;
    user_id?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
    ist_uebung?: boolean | null;
    project_services?: ForecastServiceRow[] | null;
    data?: { metadata?: ForecastMetadata | null } | null;
}

/* ═════════════════════════════════════════════════════════════════════════
 * 2. Kalenderarithmetik ohne Date
 *
 * Dieselbe Begruendung wie in cockpitLogic.calendarDay: `new Date('2026-02-31')`
 * ist kein Fehler, sondern der 3. Maerz, und `getMonth()` liest in Ortszeit.
 * Gerechnet wird deshalb auf TAGESNUMMERN (Tage seit 1970-01-01), die sich aus
 * den Ziffern des ISO-Datums ergeben — zeitzonenfrei und exakt.
 * ═══════════════════════════════════════════════════════════════════════ */

const MONTHS = 12;

const zeros = (): MonthSeries => [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

const isLeapYear = (y: number): boolean => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

const daysInMonth = (y: number, m: number): number =>
    m === 1 ? (isLeapYear(y) ? 29 : 28) : [31, 0, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m];

/**
 * Tagesnummer (Tage seit 1970-01-01) aus einem buergerlichen Datum; `m` ist
 * 0-11 wie bei Date. Verfahren nach Howard Hinnant („days_from_civil"), exakt
 * fuer den gesamten proleptischen gregorianischen Kalender.
 */
const dayNumber = (y: number, m: number, d: number): number => {
    const month = m + 1;
    const shifted = y - (month <= 2 ? 1 : 0);
    const era = Math.floor(shifted / 400);
    const yearOfEra = shifted - era * 400;
    const dayOfYear = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + d - 1;
    const dayOfEra = yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100) + dayOfYear;
    return era * 146097 + dayOfEra - 719468;
};

/** Absoluter Monatsindex, damit Monate ueber Jahresgrenzen hinweg zaehlbar sind. */
const absoluteMonth = (year: number, month: number): number => year * MONTHS + month;

/* ═════════════════════════════════════════════════════════════════════════
 * 3. Zahlen, die nie NaN werden
 * ═══════════════════════════════════════════════════════════════════════ */

/** Endliche Zahl oder 0 — `Number('')` ist 0, `Number('x')` waere NaN. */
const num = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

/** Ganzzahlige Division ohne NaN/Infinity: Nenner <= 0 ergibt 0. */
const ratio = (top: number, bottom: number): number =>
    bottom > 0 && Number.isFinite(top) ? top / bottom : 0;

/**
 * Cent als EURO fuer einen Warntext.
 *
 * Die Meldung stand vorher als „(1125000 Cent)" da — eine Zahl, die niemand
 * liest. Ohne Nachkommastellen, weil in einer Warnung die Groessenordnung
 * zaehlt und nicht der Cent.
 */
const euroFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });

const euroRounded = (cents: number): string => `${euroFormat.format(Math.round(cents / 100))} €`;

/* ═════════════════════════════════════════════════════════════════════════
 * 4. Turnus — die Regeln aus AdminDashboard.tsx:400–425, Feldnamen wortgleich
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Schrittweite des Turnus in TAGEN.
 *
 * Die ersten drei Zeilen sind die alte Regel wortgleich
 * (`intv?.type==='custom'?(intv.customDays||7):intv?.type==='daily'?1:
 * intv?.type==='biweekly'?14:7`). ERGAENZT sind `monthly` und `twice_monthly`:
 * der alte Ausdruck warf beide in den 7-Tage-Zweig, eine Monatsbegehung wurde
 * also als Wochenbegehung geplant. Solange die Schleife ohnehin nach
 * `quantityContracted` Terminen abbrach, verschob das nur die Termine; mit der
 * Deckelung ueber die Restmenge bleibt die Zahl gleich, aber sie laege in den
 * falschen Monaten. `manual` und ein fehlender Turnus bleiben bei 7 — dieselbe
 * Annahme wie bisher, und wie bisher wird sie gemeldet: der fehlende Turnus
 * erzeugt die Warnung `missing_interval` (alte Warnart `no_interval`).
 */
const rhythmStepDays = (interval: ForecastIntervalConfig | null | undefined): number => {
    const type = interval?.type;
    if (type === 'custom') return Math.max(1, Math.round(num(interval?.customDays) || 7));
    if (type === 'daily') return 1;
    if (type === 'biweekly') return 14;
    if (type === 'twice_monthly') return 15;
    if (type === 'monthly') return 30;
    return 7;
};

/**
 * Zaehlt eine Position als Begehung?
 *
 * Wortgleich die alte Zuordnung: `const asgn=(s.assignment||'additional')` und
 * danach `else if(asgn==='inspection')`. Eine Position ohne `assignment` ist
 * also KEINE Begehung, sondern faellt in den „sonstige"-Zweig.
 */
const isInspectionRow = (row: ForecastServiceRow): boolean =>
    (row.assignment || 'additional') === 'inspection';

/** Eine Zeile, so weit sie ein Deckungsobjekt tragen KANN. */
export interface FlatRateCarrier {
    /** Die Spalte aus `project_services`. */
    flat_rate?: unknown;
    /** Dieselbe Angabe, wie sie im Blob-Bestand steht. */
    flatRate?: unknown;
}

/**
 * Das Deckungsobjekt einer Zeile — EINE Leseregel fuer Forecast UND Engine.
 *
 * Die Spalte heisst `flat_rate`, der Blob-Bestand fuehrt dieselbe Angabe als
 * `flatRate`. Der Forecast las beide, die Engine nur die Spalte: bei einer
 * camelCase-Zeile sah der Forecast eine Deckung, die die Engine nicht kannte
 * — er plante die gedeckte Begehung mit 0 Cent, die Engine bewertete sie
 * voll, und die Realisierung meldete dauerhaft ein Plus, das es nie gab.
 * Deshalb liest die Engine GENAU DIESE Funktion mit (cockpitEngine importiert
 * sie); zwei gleichlautende Kopien waeren beim naechsten Feldnamen wieder
 * auseinandergelaufen.
 */
export const flatRateOf = (row: FlatRateCarrier | null | undefined): FlatRateCoverage | undefined => {
    const raw = row?.flat_rate ?? row?.flatRate;
    return raw && typeof raw === 'object' ? (raw as FlatRateCoverage) : undefined;
};

/**
 * Die rohe DB-Zeile in der Form, die `flatRateLogic` liest.
 *
 * Die Deckungslogik kennt nur camelCase (`flatRate`, `lvPosition`), die Zeile
 * aus `project_services` fuehrt beides in snake_case — hier und nur hier wird
 * uebersetzt, damit Forecast und Engine DIESELBE Zuordnung bekommen.
 */
const flatLikeRow = (row: ForecastServiceRow, id: string): FlatCoverageServiceLike => ({
    id,
    assignment: row.assignment ?? undefined,
    lvPosition: row.lv_position ?? row.lvPosition ?? undefined,
    title: row.title ?? undefined,
    flatRate: flatRateOf(row),
});

/**
 * Traegt die Position eine Monatspauschale?
 *
 * Massgeblich ist das Deckungsobjekt (`isFlatRateService`). Zusaetzlich zaehlt
 * die EINHEIT: eine Position „Monat"/„mtl." ist eine Monatspauschale, auch wenn
 * niemand eine Deckung eingetragen hat (Plan §2.7, Regel 4).
 *
 * ABGRENZUNG zu `isFlatRateService` allein: nur eine Pauschale mit echtem
 * Deckungsobjekt DECKT andere Positionen; eine Position, die bloss in Monaten
 * abgerechnet wird, wird hier nur wie eine Pauschale GERECHNET.
 */
const isFlatRateRow = (row: ForecastServiceRow, id: string): boolean =>
    isFlatRateService(flatLikeRow(row, id)) || isMonthlyUnit(row.unit);

/* ═════════════════════════════════════════════════════════════════════════
 * 5. Restmenge zu Jahresbeginn
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Menge eines Eintrags — EINE Definition fuer Engine und Forecast
 * (`cockpitLogic.altQuantity`). Zwei eigene Regeln hier und dort hiessen: der
 * Forecast plant eine Restmenge, die die Engine nie erbracht sieht.
 */
const entryQuantity = (log: ForecastLogEntry): number => altQuantity(log);

/**
 * Je Position die Summe der VOR `year` tatsaechlich erbrachten Mengen.
 *
 * EIN Durchgang durch das Protokoll je Projekt, nicht einer je Position: das
 * alte Cockpit rief `sv.find(...)` in jeder Protokollzeile (Audit-Befund 15,
 * „traege"). Ein Projekt mit 40 Positionen und 900 Zeilen kostet so 900 statt
 * 36 000 Schritte.
 *
 * Kalendertag aus demselben Feld wie im Rest des Cockpits
 * (`performedDateIso` → Leistungsdatum, Verbuchung nur als Notnagel). Ohne
 * brauchbares Datum zaehlt der Eintrag NICHT als „vor dem Jahr": ein
 * datumsloser Eintrag darf keine Restmenge wegnehmen, die vielleicht noch
 * ansteht.
 */
const usedBeforeYearByService = (logs: ForecastLogEntry[], year: number): Map<string, number> => {
    const out = new Map<string, number>();
    for (const log of logs) {
        if (!log || typeof log.serviceId !== 'string' || !log.serviceId) continue;
        if (!isWorkEntry(log)) continue;
        const qty = entryQuantity(log);
        if (qty <= 0) continue;
        const day = calendarDay(performedDateIso(log));
        if (!day || day.year >= year) continue;
        out.set(log.serviceId, (out.get(log.serviceId) || 0) + qty);
    }
    return out;
};

/**
 * Begehungen VOR `year`, die als NACHWEISZEILE auf einer Pauschale stehen.
 *
 * Seit 30.09.2026 bucht der Versand im Pauschalprojekt die Begehung als
 * Nachweis (Menge 0, 'NW-…') auf die Pauschale; die Turnusposition bekommt
 * nichts mehr. `usedBeforeYearByService` sieht davon nichts (Menge 0), die
 * Restmenge der Turnusposition bliebe ab der Umstellung stehen, und der
 * Forecast plante die schon gefahrenen Begehungen ein zweites Mal ein —
 * Termine, Auslastung und (ausserhalb gedeckter Monate) Geld.
 *
 * Dieselbe Frage wie projectLv.inspectionProofCount, nur mit Datumsgrenze:
 * jede Referenz einmal, ohne brauchbares Datum nicht „vor dem Jahr" (wie
 * `usedBeforeYearByService`).
 */
const proofsBeforeYear = (
    rows: ReadonlyArray<{ row: ForecastServiceRow; serviceId: string }>,
    logs: ForecastLogEntry[],
    year: number,
): number => {
    const isProof = inspectionProofMatcher(rows.map(({ row, serviceId }) => ({ id: serviceId, flatRate: flatRateOf(row) })));
    const refs = new Set<string>();
    for (const log of logs) {
        if (!log || !isProof(log)) continue;
        const day = calendarDay(performedDateIso(log));
        if (!day || day.year >= year) continue;
        refs.add(String(log.bookingReference).trim());
    }
    return refs.size;
};

/**
 * Stunden je TERMIN einer stundenbasierten Begehungsposition.
 *
 * Eine Begehung, die in Stunden abgerechnet wird, hat zwei verschiedene
 * Mengen: der Turnus erzeugt TERMINE, die Restmenge ist in STUNDEN. Ohne
 * Umrechnung wurde beides gleichgesetzt — 20 offene Stunden ergaben 20
 * geplante Begehungen, und die Auslastung rechnete 20 Termine statt zehn.
 *
 * Der Umrechnungsfaktor kommt aus dem Bestand des Projekts selbst: der
 * Durchschnitt der bisher gebuchten Arbeitseintraege dieser Position
 * (Σ Menge / Anzahl). Das ist die einzige Quelle, die die Gepflogenheit des
 * Projekts kennt — 2 h bei der einen Baustelle, 4 h bei der naechsten. Gibt
 * es noch keinen Eintrag, gilt 1 Stunde je Termin; damit verhaelt sich die
 * Position wie vor dieser Regel und niemand bekommt eine erfundene Zahl.
 *
 * NICHT JEDE ZEILE IST EIN TERMIN. Zwei Formen tragen eine SAMMELmenge, hinter
 * der beliebig viele Termine stecken, und beide verderben den Schnitt sofort:
 *   1. die Migrations-Sammelbuchung (`isMigrationBulkBackfill`) — eine Zeile
 *      ueber den gesamten Altbestand, kuenstlich datiert;
 *   2. die Uebernahme aus einer Alt-Rechnung (`isBackfilledAltEntry`, `ALT-`)
 *      — die Menge EINES Belegs, nicht die eines Besuchs.
 * Ein 100-Stunden-Altbestand neben einem echten 2-Stunden-Termin ergab 51 h je
 * Termin; die Restmenge von 20 Stunden reichte damit fuer NULL Termine, und
 * die Position verschwand vollstaendig aus dem Forecast. Beide zaehlen weiter
 * in `usedBeforeYearByService` — die MENGE ist ja echt, nur die Zahl der
 * Termine dahinter ist unbekannt.
 */
const hoursPerAppointment = (logs: ForecastLogEntry[], serviceId: string | null | undefined): number => {
    if (!serviceId) return 1;
    let sum = 0;
    let count = 0;
    for (const log of logs) {
        if (!log || log.serviceId !== serviceId || !isWorkEntry(log)) continue;
        if (isMigrationBulkBackfill(log) || isBackfilledAltEntry(log)) continue;
        const qty = entryQuantity(log);
        if (qty <= 0) continue;
        sum += qty;
        count += 1;
    }
    return count > 0 && sum > 0 ? sum / count : 1;
};

/* ═════════════════════════════════════════════════════════════════════════
 * 6. Der Verlauf einer Position
 * ═══════════════════════════════════════════════════════════════════════ */

/** Fenster eines Projekts im betrachteten Jahr, als Tagesnummern. */
interface YearWindow {
    /** Erster Tag: max(Projektbeginn, 1. Januar). */
    from: number;
    /** Letzter Tag: min(Projektende, 31. Dezember). */
    to: number;
    /** Erster Monat im Jahr, in dem das Projekt laeuft (0-11). */
    firstMonth: number;
    /** Letzter Monat im Jahr, in dem das Projekt laeuft (0-11). */
    lastMonth: number;
}

/**
 * Geplante Termine je Monat aus einem arithmetischen Turnus — OHNE Tagesschleife.
 *
 * Statt Tag fuer Tag zu schreiten wird je Monatsende gefragt, der WIEVIELTE
 * Termin dort erreicht ist; die Differenz zweier Monatsenden ist die Zahl der
 * Termine in diesem Monat. Das sind 12 Divisionen je Position, unabhaengig
 * davon, ob der Turnus taeglich oder monatlich ist.
 *
 * Der Anker ist der Projektbeginn — dieselbe Annahme wie in der alten
 * Simulation (`const cur=new Date(simS)`): der Turnus zaehlt ab Beginn, nicht
 * ab Jahresanfang, sonst verschoeben sich die Termine jedes Jahr.
 */
const inspectionsPerMonth = (anchor: number, step: number, window: YearWindow, year: number): MonthSeries => {
    const out = zeros();
    if (window.to < window.from || step <= 0) return out;
    const first = Math.max(0, Math.ceil(ratio(window.from - anchor, step)));
    /** Wie viele Termine liegen bis einschliesslich `day` im Fenster? */
    const upTo = (day: number): number => {
        const clamped = Math.min(day, window.to);
        if (clamped < window.from) return 0;
        const last = Math.floor(ratio(clamped - anchor, step));
        return Math.max(0, last - first + 1);
    };
    let previous = 0;
    for (let m = 0; m < MONTHS; m += 1) {
        const cumulative = upTo(dayNumber(year, m, daysInMonth(year, m)));
        out[m] = Math.max(0, cumulative - previous);
        previous = cumulative;
    }
    return out;
};

/**
 * Deckelt einen Monatsverlauf auf ein Jahresbudget und meldet, was wegfiel.
 * Der Schnitt faellt am ENDE des Jahres an: die frueheren Monate stehen
 * naeher am Turnus und sind die wahrscheinlicheren Termine.
 */
const capSeries = (series: MonthSeries, budget: number): { series: MonthSeries; cut: number } => {
    const out = zeros();
    const limit = Math.max(0, budget);
    let used = 0;
    let cut = 0;
    for (let m = 0; m < MONTHS; m += 1) {
        const wanted = series[m];
        const allowed = Math.max(0, Math.min(wanted, limit - used));
        out[m] = allowed;
        used += allowed;
        cut += wanted - allowed;
    }
    return { series: out, cut };
};

/**
 * Verteilt einen ganzzahligen Gesamtwert gleichmaessig auf `count` Monate ab
 * `startAbsoluteMonth` und legt nur die Monate von `year` in die Reihe.
 *
 * Dient CENT und MINUTEN: beide sind ganzzahlige Groessen, die am Jahresende
 * exakt ihre Summe ergeben muessen.
 *
 * Die Verteilung geht ueber die kumulierte Summe (`round(total*(i+1)/n) −
 * round(total*i/n)`), nicht ueber `round(total/n)` je Monat: sonst fehlen oder
 * entstehen bei 100 € auf 3 Monate ein paar Cent gegenueber der Gesamtsumme.
 * Gerundet wird also am ENDE, nicht in jedem Monat einzeln.
 */
const spreadEvenly = (
    total: number,
    startAbsoluteMonth: number,
    count: number,
    year: number,
    into: MonthSeries,
): void => {
    if (count <= 0 || total === 0) return;
    const yearStart = absoluteMonth(year, 0);
    let previous = 0;
    for (let i = 0; i < count; i += 1) {
        const cumulative = roundCents(ratio(total * (i + 1), count));
        const share = cumulative - previous;
        previous = cumulative;
        const index = startAbsoluteMonth + i - yearStart;
        if (index >= 0 && index < MONTHS) into[index] += share;
    }
};

/* ═════════════════════════════════════════════════════════════════════════
 * 8. Projektkopf: Status, Fenster, Verantwortlicher
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Nur AKTIVE Projekte liefern Forecast (Plan §2.7).
 *
 * `planning` ist die Pipeline und wird als eigene Kachel ausgewiesen, nicht als
 * Forecast; `on_hold` ruht; `completed`/`finished` sind abgeschlossen. Der alte
 * Forecast kannte diesen Filter gar nicht — ein seit zwei Jahren beendetes
 * Projekt lieferte weiter seinen vollen Vertragswert.
 *
 * Quelle wie im alten Cockpit: Spalte zuerst, Blob als Rueckfall
 * (`(p.status||g?.status||'planning')`).
 */
const projectStatus = (project: ForecastProject, global: ForecastGlobalData | null | undefined): string =>
    project.status || global?.status || 'planning';

/**
 * Der Beginn, ab dem gerechnet wird.
 *
 * Reihenfolge nach Plan §2.7, Regel 1: `start_date`, dann `globalData.startDate`
 * (dieselbe Paarung wie im alten Cockpit), dann der FRUEHESTE Arbeitseintrag
 * im Protokoll, dann `created_at`, dann `updated_at`. Faellt alles aus, gilt
 * der 1. Januar des Jahres — das Projekt bleibt sichtbar statt still
 * wegzufallen (Audit-Befund 7: nie stilles Verschwinden).
 */
const resolveStart = (
    project: ForecastProject,
    global: ForecastGlobalData | null | undefined,
    logs: ForecastLogEntry[],
    year: number,
): { day: { year: number; month: number; day: number }; missing: boolean } => {
    const direct = calendarDay(project.start_date) ?? calendarDay(global?.startDate);
    if (direct) return { day: direct, missing: false };

    let earliest: { year: number; month: number; day: number } | null = null;
    let earliestNumber = Number.POSITIVE_INFINITY;
    for (const log of logs) {
        if (!log || !isWorkEntry(log) || entryQuantity(log) <= 0) continue;
        const day = calendarDay(performedDateIso(log));
        if (!day) continue;
        const n = dayNumber(day.year, day.month, day.day);
        if (n < earliestNumber) {
            earliestNumber = n;
            earliest = day;
        }
    }
    const fallback = earliest ?? calendarDay(project.created_at) ?? calendarDay(project.updated_at);
    return { day: fallback ?? { year, month: 0, day: 1 }, missing: true };
};

// VERANTWORTUNG UND AUSLASTUNG: die Zuordnung zu Personen (Koordinator,
// Eigentuemer, Stellvertreter-Wochentage) macht cockpitWorkload — der
// Geld-Forecast rechnet nur noch je Projekt und Position.

/* ═════════════════════════════════════════════════════════════════════════
 * 9. buildForecast
 * ═══════════════════════════════════════════════════════════════════════ */

const addInto = (target: MonthSeries, source: MonthSeries): void => {
    for (let m = 0; m < MONTHS; m += 1) target[m] += source[m];
};

const ensureSeries = (bucket: Record<string, MonthSeries>, key: string): MonthSeries => {
    const found = bucket[key];
    if (found) return found;
    const created = zeros();
    bucket[key] = created;
    return created;
};

/**
 * Forecast und Auslastung eines Jahres.
 *
 * Alle Geldwerte sind ganzzahlige CENT, alle Reihen haben genau zwoelf Werte
 * (Index 0 = Januar). Die Funktion wirft nie: fehlende Felder, kaputte Daten
 * und leere Listen ergeben Nullen, keine NaN.
 */
export function buildForecast(input: ForecastInput): ForecastResult {
    const year = Math.trunc(num(input?.year));

    const byProject: Record<string, MonthSeries> = {};
    const byService: Record<string, Record<string, MonthSeries>> = {};
    const inspectionsByProject: Record<string, MonthSeries> = {};
    const warnings: CockpitWarning[] = [];

    const januaryFirst = dayNumber(year, 0, 1);
    const decemberLast = dayNumber(year, 11, 31);

    /**
     * Der erste Monat, der ueberhaupt noch GEPLANT wird — alles davor ist
     * vergangen.
     *
     * Die Engine zaehlt einen Pauschalmonat nur, wenn er als `FLAT-JJJJ-MM`
     * im Protokoll steht. Der Forecast plante dagegen ab dem ersten
     * Fenstermonat fortlaufend durch: fuer einen VERGANGENEN Monat ohne
     * Buchung stand damit eine Erwartung, die niemand mehr erfuellen kann,
     * und die gedeckte Begehung desselben Monats stand zugleich auf null —
     * der Monat fehlte in beiden Reihen und die Realisierung meldete ein
     * Defizit, das aus der Rechenweise kam und nicht aus der Baustelle.
     *
     * Ohne lesbares `today` gilt nichts als vergangen: lieber weiterplanen
     * wie bisher als eine Vergangenheit aus geratenen Monaten.
     */
    const todayDay = calendarDay(input?.today);
    const pastBoundary = !todayDay || todayDay.year < year ? 0 : todayDay.year > year ? MONTHS : todayDay.month;

    /** `FLAT-JJJJ-MM` eines Monats des betrachteten Jahres. */
    const flatKeyOfMonth = (month: number): string | null =>
        flatMonthKey(`${String(year).padStart(4, '0')}-${String(month + 1).padStart(2, '0')}-01`);

    const rows: ForecastProject[] = input?.projects || [];

    rows.forEach((project, projectIndex) => {
        if (!project) return;
        if (istUebungsprojekt(project)) return;

        const metadata = project.data?.metadata ?? null;
        const global = metadata?.globalData ?? null;
        if (projectStatus(project, global) !== 'active') return;

        const logs: ForecastLogEntry[] = global?.serviceLog || [];
        const projectId = project.id || `project-${projectIndex}`;
        const projectName = project.name || projectId;

        const { day: startDay, missing } = resolveStart(project, global, logs, year);
        if (missing) {
            warnings.push({
                kind: 'missing_start_date',
                projectId,
                projectName,
                text: `${projectName}: kein Projektbeginn hinterlegt — der Forecast rechnet ab ${startDay.day}.${startDay.month + 1}.${startDay.year}.`,
            });
        }
        const startNumber = dayNumber(startDay.year, startDay.month, startDay.day);

        const endDay = calendarDay(project.end_date) ?? calendarDay(global?.endDate);
        const endNumber = endDay ? dayNumber(endDay.year, endDay.month, endDay.day) : null;

        // Ueberlappung: Beginn <= 31.12. UND (kein Ende ODER Ende >= 1.1.).
        if (startNumber > decemberLast) return;
        if (endNumber !== null && endNumber < januaryFirst) return;

        const window: YearWindow = {
            from: Math.max(startNumber, januaryFirst),
            to: endNumber === null ? decemberLast : Math.min(endNumber, decemberLast),
            firstMonth: startDay.year < year ? 0 : startDay.month,
            lastMonth: endDay && endDay.year === year ? endDay.month : 11,
        };
        if (window.lastMonth < window.firstMonth) return;

        const interval = metadata?.intervalConfig ?? null;
        const step = rhythmStepDays(interval);

        const projectSeries = ensureSeries(byProject, projectId);
        const projectInspections = ensureSeries(inspectionsByProject, projectId);
        const projectServices = byService[projectId] || {};
        byService[projectId] = projectServices;
        const usedBefore = usedBeforeYearByService(logs, year);

        let cappedCents = 0;
        let cappedCount = 0;
        let withoutInterval = 0;

        /* ── DECKUNG DURCH EINE MONATSPAUSCHALE ───────────────────────────
         *
         * Eine gedeckte Begehung bringt kein eigenes Geld mehr: der Monat ist
         * mit der Pauschale bezahlt. Der Forecast wusste das nicht und plante
         * beides — 1200 € Pauschale PLUS die Begehungen, die sie enthaelt.
         * Bei zwoelf Monatspauschalen zu 100 € und einer gedeckten Begehung zu
         * 50 € stand die Erwartung um 600 € zu hoch, und die Realisierung
         * meldete dauerhaft ein Defizit, das es nie gab.
         *
         * Aufgeloest wird GENAU WIE IN DER ENGINE (`resolveFlatCoverage`), mit
         * denselben Regeln fuer scope, Selbstdeckung und Reihenfolge — zwei
         * eigene Deckungsbegriffe hiessen: der Forecast erwartet Geld fuer
         * eine Position, die die Engine nie als Umsatz sieht.
         *
         * Was BLEIBT: die Termine. Eine gedeckte Begehung wird trotzdem
         * gefahren, zaehlt in `inspectionsByProject` und kostet Auslastung.
         * Nur die Cent sind null.
         */
        // OHNE ID KEINE ZEILE — dieselbe Grenze wie in der Engine
        // (`const serviceId = text(service.id); if (!serviceId) continue;`).
        // Frueher bekam eine Position ohne id hier den Ersatzschluessel
        // `projektId#index` und wurde mitgeplant, waehrend die Engine sie nie
        // als erbracht sehen konnte: eine Erwartung ohne jede Moeglichkeit
        // der Erfuellung, die die Realisierung dauerhaft druecke.
        const serviceRows = (project.project_services || [])
            .filter((row): row is ForecastServiceRow => !!row)
            .map((row) => ({ row, serviceId: typeof row.id === 'string' ? row.id : '' }))
            .filter((entry) => entry.serviceId !== '');
        const coverage = resolveFlatCoverage(
            serviceRows.map(({ row, serviceId }) => flatLikeRow(row, serviceId)),
        );
        /** Je deckender Pauschale: die Monate, in denen sie Geld in den Forecast legt. */
        const flatPaidMonths = new Map<string, boolean[]>();
        // PAUSCHALEN ZUERST. Ihre Monate entscheiden, wo eine gedeckte Position
        // still bleibt, und die LV-Reihenfolge sagt nichts darueber, ob die
        // Pauschale vor oder hinter den Begehungen steht.
        const isCoveringFlat = ({ row, serviceId }: { row: ForecastServiceRow; serviceId: string }): boolean =>
            isFlatRateService(flatLikeRow(row, serviceId));
        const orderedRows = [
            ...serviceRows.filter(isCoveringFlat),
            ...serviceRows.filter((entry) => !isCoveringFlat(entry)),
        ];

        // NACHWEISE AUF DER PAUSCHALE gehoeren zur Turnusposition: sie sind
        // die Begehungen, die frueher dort gebucht worden waeren. Traeger ist
        // dieselbe Position, die in cockpitWorkload die Termine traegt
        // (primaryInspectionRow) — Plan und Rest meinen so denselben Turnus.
        const proofCount = proofsBeforeYear(serviceRows, logs, year);
        const proofCarrierId = proofCount > 0
            ? primaryInspectionRow(
                serviceRows.filter(({ row, serviceId }) => isInspectionRow(row) && !isFlatRateRow(row, serviceId)),
                logs,
                input?.today,
            )?.serviceId ?? null
            : null;

        orderedRows.forEach(({ row, serviceId }) => {
            // PREIS BLEIBT EURO bis zur Multiplikation. Erst auf Cent zu runden
            // und dann mit der Menge zu multiplizieren, verliert bei kleinen
            // Einzelpreisen alles: 100 Stueck zu 0,014 € sind 140 Cent, ueber
            // den gerundeten Einzelpreis (1 Cent) aber nur 100. `entryCents`
            // ist dieselbe Reihenfolge, die die Engine fuer ERBRACHT nimmt —
            // sonst ergaeben Forecast und Ist fuer dieselbe Menge verschiedene
            // Betraege.
            const unitPriceEuro = num(row.unit_price);
            const contracted = contractedQuantityOfRow(row);
            if (contracted <= 0) return;
            // Ein Nachweis ist ein TERMIN; eine Stundenposition fuehrt ihren Rest
            // in Stunden (hoursPerAppointment, dieselbe Umrechnung wie unten).
            const proofsUsed = serviceId === proofCarrierId
                ? proofCount * (isHourlyUnit(row.unit) ? hoursPerAppointment(logs, serviceId) : 1)
                : 0;
            const remaining = Math.max(0, contracted - (usedBefore.get(serviceId) || 0) - proofsUsed);

            // EIGENE REIHE JE ZEILE, erst am Ende in Position und Projekt
            // gelegt. Teilen sich zwei Zeilen dieselbe Positions-Id (ein
            // Anlagefehler, den es im Bestand gibt), schrieben beide in
            // DIESELBE Reihe, und `addInto` legte den aufgelaufenen Stand ein
            // zweites Mal ins Projekt: aus zwei Zeilen zu 1.000 EUR wurden
            // 3.000 EUR Erwartung.
            const serviceSeries = zeros();

            if (isFlatRateRow(row, serviceId)) {
                // ── Monatspauschale: Monatspreis je Ueberlappungsmonat ──
                // Ist die Position in MONATEN beauftragt, ist die Restmenge eine
                // Zahl von Monaten und deckelt sie. Eine Pauschale, die als
                // Stueck/Pauschal gefuehrt wird, hat keine Monatszahl — dann
                // gilt nur das Fenster.
                const overlapMonths = window.lastMonth - window.firstMonth + 1;
                const budget = isMonthlyUnit(row.unit) ? Math.floor(remaining) : overlapMonths;
                const monthCents = entryCents(1, unitPriceEuro);
                // Nur die Monate, in denen die Pauschale WIRKLICH Geld bringt,
                // legen eine gedeckte Position still. Eine Pauschale ohne Preis
                // deckt rechnerisch nichts ab — wuerde sie es doch, fiele die
                // Begehung ersatzlos aus dem Forecast, statt bloss nicht
                // doppelt zu zaehlen.
                const paidMonths = new Array<boolean>(MONTHS).fill(false);
                flatPaidMonths.set(serviceId, paidMonths);
                // Die AUSLASTUNG der Pauschale rechnet cockpitWorkload.buildWorkloadPlan.
                const payMonth = (m: number): void => {
                    serviceSeries[m] += monthCents;
                    if (monthCents > 0) paidMonths[m] = true;
                };

                // VERGANGENHEIT: genau die GEBUCHTEN Monate, aus derselben
                // Quelle wie die Engine (`bookedFlatMonths`). Ein vergangener
                // Monat ohne Buchung bringt kein Geld mehr — und legt die
                // Begehung dieses Monats folglich auch nicht still.
                const booked = bookedFlatMonths(logs, serviceId);
                const firstOpenMonth = Math.max(window.firstMonth, pastBoundary);
                let bookedPast = 0;
                for (let m = window.firstMonth; m < firstOpenMonth; m += 1) {
                    const key = flatKeyOfMonth(m);
                    if (!key || !booked.has(key)) continue;
                    bookedPast += 1;
                    payMonth(m);
                }

                // GEGENWART UND ZUKUNFT: fortlaufend ab dem ersten offenen
                // Monat, gedeckelt durch die Restmenge ABZUEGLICH der bereits
                // gebuchten Monate — sonst zahlte eine auf zwoelf Monate
                // beauftragte Pauschale nach sechs gebuchten Monaten noch
                // zwoelf weitere.
                const openMonths = Math.max(0, window.lastMonth - firstOpenMonth + 1);
                const payable = Math.max(0, Math.min(openMonths, budget - bookedPast));
                for (let m = firstOpenMonth; m < firstOpenMonth + payable; m += 1) payMonth(m);
                // GESTRICHEN ist nur, was die Restmenge nicht mehr hergibt.
                // Ein vergangener, nie gebuchter Monat ist keine Deckelung,
                // sondern ein Monat, den niemand gebucht hat — er gehoert
                // nicht in die Meldung „ueber der Restmenge gestrichen".
                const missed = openMonths - payable;
                if (missed > 0) {
                    cappedCount += missed;
                    cappedCents += entryCents(missed, unitPriceEuro);
                }
            } else if (isInspectionRow(row)) {
                // ── Begehungen: Turnus erzeugt die Termine, Restmenge deckelt ──
                //
                // STUNDENPOSITIONEN trennen TERMIN und MENGE: der Turnus sagt,
                // wie oft jemand hinfaehrt, die Restmenge steht in STUNDEN.
                // Gleichgesetzt ergaben 20 offene Stunden 20 Termine, 20
                // Fahrten Auslastung und den Stundensatz als Terminpreis. Der
                // Umrechnungsfaktor ist der Schnitt der bisherigen Eintraege
                // dieser Position (hoursPerAppointment).
                const hourly = isHourlyUnit(row.unit);
                const perAppointment = hourly ? hoursPerAppointment(logs, serviceId) : 1;
                // Gedeckelt wird in TERMINEN: ein halber Termin ist keine
                // Planung. Der Rest unter einem vollen Termin bleibt liegen.
                const budget = hourly ? Math.floor(ratio(remaining, perAppointment)) : remaining;
                const raw = inspectionsPerMonth(startNumber, step, window, year);
                const { series: planned, cut } = capSeries(raw, budget);
                if (!interval?.type) withoutInterval += 1;
                for (let m = 0; m < MONTHS; m += 1) {
                    if (planned[m] <= 0) continue;
                    serviceSeries[m] += entryCents(planned[m] * perAppointment, unitPriceEuro);
                    projectInspections[m] += planned[m];
                }
                if (cut > 0) {
                    cappedCount += cut;
                    cappedCents += entryCents(cut * perAppointment, unitPriceEuro);
                }
            } else {
                // ── Sonstige (Stunden, Stueck ohne Turnus, Planungsleistungen) ──
                // Restwert linear ueber die Restmonate. Laeuft das Projekt UEBER
                // das Jahr hinaus, wird ueber alle verbleibenden Projektmonate
                // verteilt und nur der Jahresanteil landet in der Reihe — sonst
                // haette ein dreijaehriger Rahmenvertrag seinen ganzen Restwert
                // im ersten Jahr stehen.
                const totalCents = entryCents(remaining, unitPriceEuro);
                const spreadStart = absoluteMonth(year, window.firstMonth);
                const spreadEnd = endDay
                    ? Math.max(spreadStart, absoluteMonth(endDay.year, endDay.month))
                    : absoluteMonth(year, 11);
                const count = spreadEnd - spreadStart + 1;
                spreadEvenly(totalCents, spreadStart, count, year, serviceSeries);
                // Die AUSLASTUNG (auch Planungs- und Stundenpositionen) rechnet
                // cockpitWorkload.buildWorkloadPlan — ohne Deckelung, ab dem
                // laufenden Monat, mit Tages-/Wochen-Einheiten.
            }

            // ── GEDECKTE MONATE AUF NULL ──────────────────────────────────
            // Erst rechnen, dann stilllegen: die Termine (oben schon gezaehlt)
            // und die Auslastung bleiben stehen, nur das GELD faellt weg — in
            // genau den Monaten, in denen die deckende Pauschale zahlt. Ein
            // Monat, den die Pauschale nicht mehr erreicht (Restmenge
            // aufgebraucht, Projekt laeuft weiter), bleibt voll bezahlt.
            const coveringFlatId = coverage.get(serviceId);
            const paidByFlat = coveringFlatId ? flatPaidMonths.get(coveringFlatId) : undefined;
            if (paidByFlat) {
                for (let m = 0; m < MONTHS; m += 1) if (paidByFlat[m]) serviceSeries[m] = 0;
            }

            addInto(ensureSeries(projectServices, serviceId), serviceSeries);
            addInto(projectSeries, serviceSeries);
        });

        if (cappedCount > 0) {
            warnings.push({
                kind: 'forecast_capped',
                projectId,
                projectName,
                count: cappedCount,
                cents: cappedCents,
                text: `${projectName}: ${cappedCount} geplante Leistungen über der Restmenge gestrichen (${euroRounded(cappedCents)}).`,
            });
        }

        // KEIN TURNUS HINTERLEGT: der Forecast nimmt eine Woche an (siehe
        // rhythmStepDays). Diese Annahme verschiebt die Termine des ganzen
        // Jahres und muss deshalb sichtbar sein — im alten Cockpit hiess die
        // Meldung `no_interval` und ist beim Umbau verlorengegangen. EINMAL je
        // Projekt, mit der Zahl der betroffenen Positionen.
        if (withoutInterval > 0) {
            warnings.push({
                kind: 'missing_interval',
                projectId,
                projectName,
                count: withoutInterval,
                text: `${projectName}: Begehung ohne Intervall, Wochenturnus angenommen (${withoutInterval} Position(en)).`,
            });
        }
    });

    // AUSLASTUNG getrennt vom Geld: PLAN aus Turnus/Wochentagen ohne
    // Restmengen-Deckel, IST aus den Protokollen nach Ausfuehrendem
    // (Betreiberregel max(Ist, Plan), siehe cockpitWorkload.ts).
    const workloadMinutes = buildWorkloadPlan(input);
    const workloadIstMinutes = buildWorkloadIst(input);

    return { year, byProject, byService, inspectionsByProject, workloadMinutes, workloadIstMinutes, warnings };
}

/** Summe einer Monatsreihe — der Kuerze halber, damit Aufrufer nicht jedes Mal reduzieren. */
export const sumSeries = (series: MonthSeries | undefined | null): number =>
    (series || []).reduce((acc, v) => acc + (Number.isFinite(v) ? v : 0), 0);
